import { createContext, useContext, useState, useCallback } from "react";

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = "info", duration = 4000) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  const removeToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  const icons = {
    success: "fa-circle-check",
    error:   "fa-circle-exclamation",
    warning: "fa-triangle-exclamation",
    info:    "fa-circle-info",
  };

  const colors = {
    success: "#0a9b7c",
    error:   "#ef4444",
    warning: "#f59e0b",
    info:    "#60a5fa",
  };

  const backgrounds = {
    success: "linear-gradient(135deg, #0a2e22, #0d3d2a)",
    error:   "linear-gradient(135deg, #2e0a0a, #3d1010)",
    warning: "linear-gradient(135deg, #2e1f0a, #3d2a0d)",
    info:    "linear-gradient(135deg, #0a1a2e, #0d2040)",
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      <style>{`
        @keyframes toastIn {
          from { transform: translateX(-50%) translateY(-30px); opacity: 0; scale: 0.85; }
          to   { transform: translateX(-50%) translateY(0);     opacity: 1; scale: 1;    }
        }
        @keyframes toastOut {
          from { transform: translateX(-50%) translateY(0);     opacity: 1; scale: 1;    }
          to   { transform: translateX(-50%) translateY(-30px); opacity: 0; scale: 0.85; }
        }
        @keyframes progressBar {
          from { width: 100%; }
          to   { width: 0%;   }
        }
        .toast-item {
          position: relative;
          display: flex;
          align-items: flex-start;
          gap: 14px;
          padding: 16px 20px;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06);
          font-family: sans-serif;
          font-size: 14px;
          line-height: 1.5;
          color: #fff;
          min-width: 320px;
          max-width: 420px;
          overflow: hidden;
          backdrop-filter: blur(20px);
          animation: toastIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        .toast-icon-wrap {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 16px;
        }
        .toast-close {
          background: none;
          border: none;
          color: rgba(255,255,255,0.4);
          cursor: pointer;
          padding: 0;
          font-size: 14px;
          flex-shrink: 0;
          margin-left: auto;
          transition: color 0.2s;
          margin-top: 2px;
        }
        .toast-close:hover { color: rgba(255,255,255,0.9); }
        .toast-progress {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          border-radius: 0 0 16px 16px;
        }
        .toast-title {
          font-weight: 700;
          font-size: 13px;
          margin-bottom: 2px;
          letter-spacing: 0.3px;
        }
        .toast-message {
          font-size: 13px;
          color: rgba(255,255,255,0.8);
        }
      `}</style>

      {/* ── Toast Container ── */}
      <div style={{
        position: "fixed",
        top: "30px",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 999999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "12px",
        pointerEvents: "none",
      }}>
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="toast-item"
            style={{
              background: backgrounds[toast.type] || backgrounds.info,
              borderLeft: `4px solid ${colors[toast.type] || colors.info}`,
              pointerEvents: "all",
            }}
          >
            {/* Icon */}
            <div
              className="toast-icon-wrap"
              style={{ background: `${colors[toast.type]}22` }}
            >
              <i
                className={`fa-solid ${icons[toast.type] || icons.info}`}
                style={{ color: colors[toast.type] || colors.info }}
              ></i>
            </div>

            {/* Text */}
            <div style={{ flex: 1 }}>
              <p className="toast-title" style={{ color: colors[toast.type] || colors.info }}>
                {toast.type === "success" ? "Success"
               : toast.type === "error"   ? "Error"
               : toast.type === "warning" ? "Warning"
               : "Info"}
              </p>
              <p className="toast-message">{toast.message}</p>
            </div>

            {/* Close */}
            <button className="toast-close" onClick={() => removeToast(toast.id)}>
              <i className="fa-solid fa-xmark"></i>
            </button>

            {/* Progress bar */}
            <div
              className="toast-progress"
              style={{
                background: colors[toast.type] || colors.info,
                animation: `progressBar 4s linear forwards`,
              }}
            ></div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
};